import { Fragment, memo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { determineCardType, isCreditCardAmex } from "app/utils/creditCardUtils";
import { paymentTypesValues } from "app/utils/propTypes";
import { CB_NUMBER_MAX_LENGTH, CREDIT_CARDS_TYPES, PAYMENT_METHODS } from "app/constants";

import "./CreditCardFieldset.scss";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import InputFormik from "app/pages/.shared/form/InputFormik";
import { useField, useFormikContext } from "formik";
import { dependantPaymentCardFields } from "app/pages/Booking/Payment/paymentSchema";
import last from "lodash/last";
import { isEmpty } from "lodash";
import get from "lodash/get";

const CreditCardFieldset = ({
	showAmountToPay,
	showAmountToPayNote,
	isAmountToPayRequired,
	selectedPaymentType,
	isAmexAvailable,
}) => {
	const cardNumberInputName =
		selectedPaymentType === PAYMENT_METHODS.BCMC
			? "payment.cardBCMC.number"
			: "payment.card.number";
	const [field] = useField(cardNumberInputName);
	const [cvcMaxLength, setCvcMaxLength] = useState(3);
	const [cardType, setCardType] = useState(undefined);
	const { values, setFieldError, errors } = useFormikContext();

	useEffect(() => {
		if (field?.value) {
			setCardType(determineCardType(field?.value));
		}
	}, [field?.value]);

	const handleCreditCardInformationChange = event => {
		const { name: touchedFieldName } = event.target;

		const name = last(touchedFieldName.split("."));
		const cardPathName = selectedPaymentType === PAYMENT_METHODS.BCMC ? "cardBCMC" : "card";
		dependantPaymentCardFields.forEach(fieldName => {
			if (fieldName !== name) {
				setFieldError(`payment.${cardPathName}.${fieldName}`);
			}
			if (isEmpty(get(errors.payment, `.${cardPathName}.${fieldName}`))) {
				setFieldError("payment");
			}
		});
	};

	const changeCvcField = event => {
		handleCreditCardInformationChange(event);
		if (values?.payment?.card?.number) {
			setCvcMaxLength(isCreditCardAmex(values.payment.card.number) ? 4 : 3);
		}
	};

	// for data-browsing-ignore usage, see https://help.crisp.chat/en/article/how-to-prevent-elements-from-being-tracked-by-magicbrowse-ju7mcg/
	return (
		<div className="creditcard" data-browsing-ignore>
			{selectedPaymentType === PAYMENT_METHODS.CASINO && cardType === "amex" && (
				<div className="creditcard__note">
					<i className="icon icon--info" />
					<FormattedMessage
						id="error.payment.external.amex.not.supported.2"
						values={{ count: 4 }}
					/>
				</div>
			)}
			{showAmountToPay && (
				<div className="creditcard__item creditcard__item--amount">
					<InputFormik
						type="number"
						label={
							<span>
								<FormattedMessage id="creditcard.amount.label" />
								{isAmountToPayRequired ? "*" : ""}
							</span>
						}
						data-testid="creditcard-amount-input"
						name="payment.card.amount"
						id="creditcard-amount"
					/>
					{showAmountToPayNote && (
						<div className="creditcard__note">
							<FormattedMessage id="creditcard.amount.help.label" />
						</div>
					)}
				</div>
			)}
			<div className="creditcard__item creditcard__item--holdername">
				<InputFormik
					data-testid="creditcard-holdername-input"
					label={<FormattedMessage id="creditcard.holdername.label" />}
					name={
						selectedPaymentType === PAYMENT_METHODS.BCMC
							? "payment.cardBCMC.holderName"
							: "payment.card.holderName"
					}
					autoComplete="cc-name"
					id="creditcard-holdername"
				/>
			</div>
			<div className="creditcard__item creditcard__item--number">
				<InputFormik
					data-testid="creditcard-number-input"
					type="number"
					data-field-name="number"
					label={<FormattedMessage id="creditcard.number.label" />}
					name={
						selectedPaymentType === PAYMENT_METHODS.BCMC
							? "payment.cardBCMC.number"
							: "payment.card.number"
					}
					id={
						selectedPaymentType === PAYMENT_METHODS.BCMC
							? "creditcard-bcmc-number"
							: "creditcard-number"
					}
					autoComplete="cc-number"
					maxLength={CB_NUMBER_MAX_LENGTH}
					onChange={handleCreditCardInformationChange}
				/>

				<div className="creditcard__type-picto">
					<i className={`icon icon--${cardType}-small`} />
				</div>
			</div>

			<div className="creditcard__item creditcard__item--expiration-month">
				<InputFormik
					data-testid="creditcard-expmonth-input"
					type="number"
					id="expiration-month"
					name={
						selectedPaymentType === PAYMENT_METHODS.BCMC
							? "payment.cardBCMC.expiryMonth"
							: "payment.card.expiryMonth"
					}
					data-field-name="expiryMonth"
					autocomplete="cc-exp-month"
					maxLength={2}
					label={<FormattedMessage id="creditcard.expiryMonth.label" />}
					onChange={handleCreditCardInformationChange}
				/>
			</div>

			<div className="creditcard__item creditcard__item--expiration-year">
				<InputFormik
					data-testid="creditcard-expyear-input"
					type="number"
					id="expiration-year"
					name={
						selectedPaymentType === PAYMENT_METHODS.BCMC
							? "payment.cardBCMC.expiryYear"
							: "payment.card.expiryYear"
					}
					data-field-name="expiryYear"
					autocomplete="cc-exp-year"
					maxLength={2}
					label={<FormattedMessage id="creditcard.expiryYear.label" />}
					onChange={handleCreditCardInformationChange}
				/>
			</div>
			{selectedPaymentType !== PAYMENT_METHODS.BCMC && (
				<div className="creditcard__item creditcard__item--cvc">
					<InputFormik
						data-testid="creditcard-cvc-input"
						type="number"
						label={
							<FormattedMessage
								id={
									cardType === CREDIT_CARDS_TYPES.MAESTRO
										? "creditcard.maestro.cvc.label"
										: "creditcard.cvc.label"
								}
							/>
						}
						name="payment.card.cvc"
						data-field-name="cvc"
						id="creditcard-cvc"
						maxLength={cvcMaxLength}
						autoComplete="cc-csc"
						onChange={changeCvcField}
					/>

					<div className="creditcard__info-picto">
						<FloatingButton
							position="top"
							popoverClassName="creditcard__popover"
							floatingContent={
								<div className="cvc-info">
									<div className="cvc-info__item">
										<FormattedMessage id="payment.cvc.text" />
									</div>
									<Fragment>
										{isAmexAvailable && (
											<div className="cvc-info__item">
												<FormattedMessage id="payment.cvc.amex.text" />
											</div>
										)}

										<div className="cvc-info__pictos">
											<i className="icon icon--cvc-cb" />
											{isAmexAvailable && (
												<i className="icon icon--cvc-amex" />
											)}
										</div>
									</Fragment>
								</div>
							}
							referenceComponent={<i className="icon icon--info-grey" />}
							floatingOffsetMobile={[-11, 0]}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

CreditCardFieldset.propTypes = {
	selectedPaymentType: PropTypes.oneOfType([paymentTypesValues]),
	showAmountToPay: PropTypes.bool,
	showAmountToPayNote: PropTypes.bool,
	isAmountToPayRequired: PropTypes.bool,
	isAmexAvailable: PropTypes.bool,
};

export default memo(CreditCardFieldset);
